import { GRID } from '../core/config.js';
import { BRICK_SCORE, SCORE } from '../core/config.js';

/**
 * The brick grid for one round. Bricks live in a flat array indexed by
 * row * cols + col so collision lookups stay O(1).
 */

export const BRICK_KIND = Object.freeze({
  NORMAL: 'normal',
  SILVER: 'silver',
  GOLD: 'gold',
});

/** Silver bricks get tougher the deeper you go, same as the arcade original. */
function silverHp(round) {
  return 2 + Math.floor((round - 1) / 8);
}

function makeBrick(col, row, code, round) {
  const kind =
    code === 'G' ? BRICK_KIND.GOLD : code === 'S' ? BRICK_KIND.SILVER : BRICK_KIND.NORMAL;
  const hp = kind === BRICK_KIND.SILVER ? silverHp(round) : 1;
  return {
    col,
    row,
    code,
    kind,
    x: GRID.originX + col * GRID.cellW,
    y: GRID.originY + row * GRID.cellH,
    w: GRID.cellW,
    h: GRID.cellH,
    hp,
    maxHp: hp,
    alive: true,
    /** Counts down after a hit so the renderer can flash the brick. */
    flash: 0,
    points:
      kind === BRICK_KIND.SILVER
        ? SCORE.silverPerRound * round
        : (BRICK_SCORE[code] ?? 50),
  };
}

/**
 * @param {{rows: string[], name: string, boss?: boolean}} level
 * @param {number} round 1-based round number
 */
export function buildField(level, round) {
  const rows = level.rows.length;
  const cells = new Array(GRID.cols * rows).fill(null);
  let breakable = 0;

  for (let row = 0; row < rows; row += 1) {
    const line = level.rows[row];
    for (let col = 0; col < GRID.cols; col += 1) {
      const code = line[col];
      if (!code || code === '.') continue;
      const brick = makeBrick(col, row, code, round);
      cells[row * GRID.cols + col] = brick;
      if (brick.kind !== BRICK_KIND.GOLD) breakable += 1;
    }
  }

  return {
    cols: GRID.cols,
    rows,
    cells,
    /** Live count of bricks that can still be destroyed. */
    get breakable() {
      return breakable;
    },
    /** Bottom edge of the lowest brick, used to keep capsules honest. */
    bottom: GRID.originY + rows * GRID.cellH,
    at(col, row) {
      if (col < 0 || col >= GRID.cols || row < 0 || row >= rows) return null;
      const brick = cells[row * GRID.cols + col];
      return brick && brick.alive ? brick : null;
    },
    forEachAlive(fn) {
      for (let i = 0; i < cells.length; i += 1) {
        const brick = cells[i];
        if (brick && brick.alive) fn(brick);
      }
    },
    isCleared() {
      return breakable <= 0;
    },
    /**
     * Apply one point of damage.
     * @returns {'blocked'|'damaged'|'destroyed'}
     */
    damage(brick) {
      if (!brick.alive) return 'blocked';
      if (brick.kind === BRICK_KIND.GOLD) {
        brick.flash = 0.09;
        return 'blocked';
      }
      brick.hp -= 1;
      brick.flash = 0.09;
      if (brick.hp > 0) return 'damaged';
      brick.alive = false;
      breakable -= 1;
      return 'destroyed';
    },
    tick(dt) {
      for (let i = 0; i < cells.length; i += 1) {
        const brick = cells[i];
        if (brick && brick.flash > 0) brick.flash = Math.max(0, brick.flash - dt);
      }
    },
  };
}

/** Convert a world x/y into grid coordinates (may be out of range). */
export function toGrid(x, y) {
  return {
    col: Math.floor((x - GRID.originX) / GRID.cellW),
    row: Math.floor((y - GRID.originY) / GRID.cellH),
  };
}
